import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  FiHome, FiUsers, FiShield, FiUser, 
  FiChevronRight, FiTrendingUp, FiLogOut,
  FiMessageSquare, FiLifeBuoy, FiDollarSign,
  FiGlobe, FiBox, FiBriefcase
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import AdminTopBar from './AdminTopBar';
import OneSignal from 'react-onesignal';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  
  const [activeCategory, setActiveCategory] = useState('dashboard');
  const [completedJobsCount, setCompletedJobsCount] = useState(0);

  // แปลงสิทธิ์พนักงาน
  const getPermissions = () => {
    if (!user) return [];
    if (user.role === 'owner') {
      return ['dashboard', 'chat', 'manage_jobs', 'customers', 'employees', 'finance', 'helpdesk', 'roles'];
    }
    if (!user.permissions) return [];
    try {
      const parsed = typeof user.permissions === 'string' ? JSON.parse(user.permissions) : user.permissions;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  const userPerms = getPermissions();

  // ตรวจสิทธิ์การเข้าถึง Category หลัก
  const hasCategoryAccess = (cat) => {
    if (!user) return false;
    if (user.role === 'owner' || user.role === 'it') return true;
    
    switch (cat) {
      case 'dashboard':
        return userPerms.includes('dashboard');
      case 'chat':
        return userPerms.includes('chat');
      case 'customers':
        return userPerms.includes('manage_jobs') || userPerms.includes('customers');
      case 'homepage':
        return user.role === 'owner' || user.role === 'it' || user.role === 'admin';
      case 'stock':
        return userPerms.includes('employees') || userPerms.includes('customers') || user.role === 'admin';
      case 'hr':
        return userPerms.includes('employees') || user.role === 'admin';
      case 'finance':
        return userPerms.includes('finance');
      case 'helpdesk':
        return userPerms.includes('helpdesk');
      case 'system':
        return userPerms.includes('roles');
      default:
        return false;
    }
  };

  // ตรวจสิทธิ์การเข้าถึงเมนูย่อย (Sub-menu)
  const hasSubMenuAccess = (to) => {
    if (!user) return false;
    if (user.role === 'owner' || user.role === 'it') return true;
    
    switch (to) {
      case '/admin/data/customers':
        return userPerms.includes('customers');
      case '/admin/data/employees':
        return userPerms.includes('employees');
      case '/admin/roles':
        return userPerms.includes('roles');
      case '/admin/products':
      case '/admin/profile':
        return true; 
      case '/admin/commissions':
      case '/admin/logs':
      case '/admin/settings':
      case '/admin/web-manager':
      case '/admin/sales-data':
      case '/admin/ai-workflow':
      case '/admin/kpi':
      case '/admin/hr':
      case '/admin/exec-dashboard':
      case '/admin/google-drive':
        return user.role === 'owner' || user.role === 'it' || user.role === 'admin';
      default:
        return true;
    }
  };

  useEffect(() => {
    fetch(`${API_URL}?action=get_all_jobs`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data) {
          setCompletedJobsCount(data.data.filter(job => job.status === 'completed').length);
        }
      })
      .catch(err => console.error("Error:", err));
  }, []);

  useEffect(() => {
    const runOneSignal = async () => {
      if (user) {
        try {
          const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          await OneSignal.init({ 
            appId: 'ae754e9c-6ce2-4520-a074-284baef0fb3c', 
            allowLocalhostAsSecureOrigin: isLocalhost 
          });
          await OneSignal.login(user.uid); 
          await OneSignal.Slidedown.promptPush(); 
        } catch (error) {
          console.error('OneSignal Admin Initialization Error:', error);
        }
      }
    };
    runOneSignal();
  }, [user]);

  // เลือกหมวดหมู่แรกที่เข้าถึงได้เป็นค่าเริ่มต้นเมื่อโหลดเสร็จ
  useEffect(() => {
    const categories = ['dashboard', 'customers', 'homepage', 'stock', 'hr', 'chat', 'finance', 'helpdesk', 'system'];
    const firstAllowed = categories.find(cat => hasCategoryAccess(cat));
    if (firstAllowed) {
      setActiveCategory(firstAllowed);
    }
  }, [user]);

  const menuConfig = {
    dashboard: { to: '/admin/dashboard', label: 'Dashboard' },
    customers: { icon: FiUsers, label: 'การจัดการลูกค้า', sub: [
      { to: '/admin/data/customers', label: 'ข้อมูลลูกค้า (CRM)' },
      { to: '/admin/jobs/approve', label: 'จัดการคิว (อนุมัติจอง)' },
      { to: '/admin/blocked-dates', label: 'จัดการวันคิวเต็ม (Slots)' },
      { to: '/admin/jobs/all', label: 'ข้อมูลงานทั้งหมด' },
      { to: '/admin/jobs/create', label: 'คีย์แจ้งงานใหม่' },
      { to: '/admin/jobs/inspect', label: 'ประเมินหน้างานช่าง' }
    ]},
    homepage: { icon: FiGlobe, label: 'จัดการหน้าบ้านหลัก', sub: [
      { to: '/admin/web-manager', label: 'ปรับแต่งเนื้อหาเว็บ (CMS)' },
      { to: '/admin/google-drive', label: 'Google Drive คลังรูป' }
    ]},
    stock: { icon: FiBox, label: 'คลังสินค้า & สต๊อก', sub: [
      { to: '/admin/products', label: 'สต๊อกสินค้าแอร์บ้าน' },
      { to: '/admin/sales-data', label: 'ข้อมูลการขาย/สต๊อกเซลส์' }
    ]},
    hr: { icon: FiBriefcase, label: 'ฝ่ายบุคคล (HR)', sub: [
      { to: '/admin/data/employees', label: 'ข้อมูลพนักงาน' },
      { to: '/admin/commissions', label: 'ค่าคอมมิชชันแนะนำลูกค้า' },
      { to: '/admin/hr', label: 'ฝ่ายบุคคล & ขาดลาสาย' },
      { to: '/admin/kpi', label: 'ประเมินผลงาน & KPI' },
      { to: '/admin/roles', label: 'จัดการสิทธิ์เข้าใช้งาน' }
    ]},
    chat: { icon: FiMessageSquare, label: 'Chat & Marketing', sub: [
      { to: '/admin/chat', label: 'รวมแชทลูกค้า (Chat)' },
      { to: '/admin/facebook-automation', label: 'โพสต์แฟนเพจอัตโนมัติ' }
    ]},
    finance: { icon: FiDollarSign, label: 'ประวัติ & การเงิน', sub: [
      { to: '/admin/finance', label: 'ประวัติ & การเงิน' },
      { to: '/admin/finance/invoice', label: 'ออกใบเสร็จ/ใบกำกับภาษี' }
    ]},
    helpdesk: { icon: FiLifeBuoy, label: 'IT Support', sub: [
      { to: '/admin/helpdesk/create', label: 'แจ้งปัญหา / ขอเพิ่มระบบ' },
      { to: '/admin/helpdesk/tickets', label: 'ประวัติตั๋ว (Tickets)' }
    ]},
    system: { icon: FiShield, label: 'ฝ่ายบริหาร', sub: [
      { to: '/admin/exec-dashboard', label: 'แดชบอร์ดฝ่ายบริหาร' },
      { to: '/admin/export-data', label: 'ส่งออกข้อมูลบริษัท' },
      { to: '/admin/seo', label: 'ตั้งค่าระบบ SEO หน้าเว็บ' },
      { to: '/admin/settings', label: 'ตั้งค่าระบบ API' }, 
      { to: '/admin/logs', label: 'ระบบ Log การใช้งาน' },
      { to: '/admin/ai-workflow', label: 'AI Agent Workflows' },
      { to: '/admin/profile', label: 'ตั้งค่าโปรไฟล์' }
    ]}
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return { text: 'Hello Morning 🌅', sub: 'สวัสดีตอนเช้าครับ' };
    if (hour >= 12 && hour < 17) return { text: 'Hello Afternoon ☀️', sub: 'สวัสดีตอนบ่ายครับ' };
    if (hour >= 17 && hour < 22) return { text: 'Hello Evening 🌇', sub: 'สวัสดีตอนเย็นครับ' };
    return { text: 'Hello Night 🌌', sub: 'สวัสดีตอนดึกครับ' };
  };

  const getFilteredMenus = () => {
    if (activeCategory === 'dashboard') {
      return [{ to: '/admin/dashboard', label: 'Dashboard' }];
    }
    return (menuConfig[activeCategory]?.sub || []).filter(item => hasSubMenuAccess(item.to));
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-[#0b0f19] transition-colors duration-300">
      
      {/* 1. Sidebar ซ้ายสุด */}
      <aside className="w-14 bg-[#131825] border-r border-[#1e2330] flex flex-col items-center py-4 gap-3 z-30">
        <div className="w-9 h-9 rounded-xl bg-white dark:bg-[#0f172a] border border-gray-250 dark:border-[#334155] flex items-center justify-center overflow-hidden mb-2 p-1 shadow-sm">
          <img 
            src="/logo.png" 
            alt="TM Logo" 
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onError={(e) => { 
              e.target.onerror = null; 
              e.target.src = 'https://www.thundermaxhome.com/logo.png'; 
            }} 
          />
        </div>
        
        {hasCategoryAccess('dashboard') && (
          <button onClick={() => setActiveCategory('dashboard')} className={`p-2 rounded-lg transition-colors ${activeCategory === 'dashboard' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-white hover:bg-[#1e2330]'}`} title="Dashboard"><FiHome size={16} /></button>
        )}
        {hasCategoryAccess('customers') && (
          <button onClick={() => setActiveCategory('customers')} className={`p-2 rounded-lg transition-colors ${activeCategory === 'customers' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-white hover:bg-[#1e2330]'}`} title="การจัดการลูกค้า"><FiUsers size={16} /></button>
        )}
        {hasCategoryAccess('homepage') && (
          <button onClick={() => setActiveCategory('homepage')} className={`p-2 rounded-lg transition-colors ${activeCategory === 'homepage' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-white hover:bg-[#1e2330]'}`} title="จัดการหน้าบ้านหลัก"><FiGlobe size={16} /></button>
        )}
        {hasCategoryAccess('stock') && (
          <button onClick={() => setActiveCategory('stock')} className={`p-2 rounded-lg transition-colors ${activeCategory === 'stock' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-white hover:bg-[#1e2330]'}`} title="คลังสินค้า & สต๊อก"><FiBox size={16} /></button>
        )}
        {hasCategoryAccess('hr') && (
          <button onClick={() => setActiveCategory('hr')} className={`p-2 rounded-lg transition-colors ${activeCategory === 'hr' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-white hover:bg-[#1e2330]'}`} title="ฝ่ายบุคคล (HR)"><FiBriefcase size={16} /></button>
        )}
        {hasCategoryAccess('chat') && (
          <button onClick={() => setActiveCategory('chat')} className={`p-2 rounded-lg transition-colors ${activeCategory === 'chat' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-white hover:bg-[#1e2330]'}`} title="Chat & Marketing"><FiMessageSquare size={16} /></button>
        )}
        {hasCategoryAccess('finance') && (
          <button onClick={() => setActiveCategory('finance')} className={`p-2 rounded-lg transition-colors ${activeCategory === 'finance' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-white hover:bg-[#1e2330]'}`} title="ประวัติ & การเงิน"><FiDollarSign size={16} /></button>
        )}
        {hasCategoryAccess('helpdesk') && (
          <button onClick={() => setActiveCategory('helpdesk')} className={`p-2 rounded-lg transition-colors ${activeCategory === 'helpdesk' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-white hover:bg-[#1e2330]'}`} title="IT Support"><FiLifeBuoy size={16} /></button>
        )}
        {hasCategoryAccess('system') && (
          <button onClick={() => setActiveCategory('system')} className={`p-2 rounded-lg transition-colors ${activeCategory === 'system' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-white hover:bg-[#1e2330]'}`} title="System Settings"><FiShield size={16} /></button>
        )}
      </aside>

      {/* 2. Sidebar ขวา Sub-menu */}
      <aside className="w-44 bg-white dark:bg-[#1e293b] border-r border-gray-200 dark:border-[#334155] flex flex-col p-3 shrink-0">
        
        {/* Box ด้านบนสุด: ระบบ Hello morning ตามเวลา */}
        <div className="mb-3 p-3 rounded-xl bg-gradient-to-br from-indigo-500/15 to-blue-500/5 border border-indigo-500/10 text-left select-none">
          <p className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
            {getGreeting().text}
          </p>
          <p className="text-[8px] text-gray-400 dark:text-gray-500 font-bold mt-0.5">
            {getGreeting().sub}
          </p>
        </div>

        <h2 className="text-[9px] font-bold text-gray-400 uppercase mb-2 pl-1">
          {menuConfig[activeCategory]?.label || activeCategory}
        </h2>

        <div className="flex-1 space-y-0.5 overflow-y-auto">
          {getFilteredMenus().map((item, idx) => {
            const isExternal = item.to.startsWith('http');
            if (isExternal) {
              return (
                <a 
                  key={idx} 
                  href={item.to} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center justify-between px-3 py-1.5 rounded text-[11px] text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#131825] transition-colors font-bold"
                >
                  {item.label} <span className="text-[9px] opacity-60">↗</span>
                </a>
              );
            }
            return (
              <NavLink key={idx} to={item.to} className={({isActive}) => `flex items-center justify-between px-3 py-1.5 rounded text-[11px] transition-colors ${isActive ? 'bg-indigo-50 dark:bg-[#2d1b69] text-indigo-600 dark:text-[#c4b5fd] font-bold' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#131825]'}`}>
                {item.label}
              </NavLink>
            );
          })}
        </div>

        {/* Status Card ด้านล่าง */}
        <div className="bg-gray-900 dark:bg-[#0f172a] rounded-lg p-2.5 text-white shadow-sm mt-3">
           <div className="flex items-center gap-1 opacity-70 mb-0.5"><FiTrendingUp size={10}/> <span className="text-[8px] uppercase font-bold">Jobs Done</span></div>
           <p className="text-base font-black">{completedJobsCount}</p>
        </div>
        <button onClick={logout} className="mt-3 w-full flex items-center justify-center gap-2 py-1.5 rounded bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 text-[10px] text-red-600 dark:text-red-400 font-bold transition-colors">
          <FiLogOut size={12} /> ออกจากระบบ
        </button>
        <div className="text-[8px] text-center text-gray-400 dark:text-gray-500 mt-2 font-mono leading-normal">
          v3.5.3 - [เพิ่มเครื่องมือ SEO คอนโซล & พรีวิวจุดขายเว็บ]
        </div>
      </aside>

      {/* 3. Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <AdminTopBar />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50 dark:bg-[#0b0f19]">
          {children}
        </main>
      </div>
    </div>
  );
}